/**
 * Fecha de registro de un asistente de CREACIÓN: sigue a «ahora» hasta que el
 * operario la toca.
 *
 * Por qué hace falta: `DateTime` escribe su valor en el formulario al MONTAR,
 * `useCacheFormInSessionStorage` guarda el formulario entero en el borrador, y
 * el loader lo reinyecta. Así la hora automática de la primera visita quedaba
 * congelada para siempre en un borrador abandonado, indistinguible de una hora
 * elegida a propósito.
 *
 * La solución es guardar, junto a la fecha, la fecha AUTOMÁTICA que se le
 * asignó (`registerInitDateAuto`). Si siguen siendo iguales, el operario no la
 * tocó y se renueva; si difieren, la eligió él y se respeta.
 *
 * Se distingue por VALOR y no por eventos porque `DateTime` reescribe al
 * montar exactamente lo que recibe (`new Date(iso).toISOString()` es
 * idempotente y no toca segundos ni milisegundos): una fecha sin tocar sigue
 * siendo byte a byte igual a su marcador.
 *
 * El marcador no llega al backend: todos los `saveData` de creación toman
 * `registerInitDate` campo a campo, ninguno vuelca el borrador entero.
 *
 * 🔴 SOLO para loaders de CREACIÓN. En edición la fecha es la del registro
 * guardado y no se toca.
 *
 * Spec: docs/superpowers/specs/2026-09-23-fecha-registro-fresca-design.md
 */
export const REGISTER_DATE_AUTO_KEY = 'registerInitDateAuto';

type RegisterDateDraft = {
  registerInitDate?: string | null;
  registerInitDateAuto?: string | null;
};

export type FreshRegisterDate = {
  registerInitDate: string;
  registerInitDateAuto: string;
};

export function resolveFreshRegisterDate(
  draft: RegisterDateDraft | null | undefined,
  now: Date = new Date()
): FreshRegisterDate {
  const chosen = draft?.registerInitDate;
  const auto = draft?.registerInitDateAuto;

  // Solo se respeta una fecha si hay marcador Y difiere de él: es la única
  // prueba de que la puso el operario. Sin marcador (borradores anteriores a
  // este arreglo) la fecha la escribió `DateTime` al montar → automática.
  if (chosen && auto && chosen !== auto) {
    return { registerInitDate: chosen, registerInitDateAuto: auto };
  }

  const nowIso = now.toISOString();
  return { registerInitDate: nowIso, registerInitDateAuto: nowIso };
}
